import {
  Callout,
  Dialog,
  DropZone,
  type DropZoneFile,
} from '@aecom-ai-engineering/react-components';
import { Info } from 'lucide-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LegalWarning } from '@/components/legal-warning';

import { TRANSCRIPT_UPLOAD_ACCEPT, type UploadErrorDetails } from '../utils/transcript-upload';

const T_PREFIX = 'meeting-secretary.meetings.';
const WRONG_MEETING_KEY = `${T_PREFIX}transcript-upload-error-wrong-meeting`;
const WRONG_MEETING_NAMED_KEY = `${T_PREFIX}transcript-upload-error-wrong-meeting-named`;

interface UploadTranscriptDialogProps {
  open: boolean;
  onClose: () => void;
  /** Resolves true once the file was accepted. The dialog closes then. */
  onUpload: (file: File) => Promise<boolean>;
  isUploading: boolean;
  /** An i18n key. */
  errorKey: string | null;
  errorDetails: UploadErrorDetails;
  onClearError: () => void;
}

const formatFileSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Where the user gets the AECOM legal warning before handing a transcript file over. */
export const UploadTranscriptDialog: FC<UploadTranscriptDialogProps> = ({
  open,
  onClose,
  onUpload,
  isUploading,
  errorKey,
  errorDetails,
  onClearError,
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);

  const close = () => {
    setFile(null);
    onClearError();
    onClose();
  };

  const submit = async (picked: File) => {
    setFile(picked);
    if (await onUpload(picked)) close();
  };

  // A file for another meeting names the one it belongs to, when the backend says which.
  const { foundTitle } = errorDetails;
  const messageKey =
    errorKey === WRONG_MEETING_KEY && foundTitle ? WRONG_MEETING_NAMED_KEY : errorKey;
  const errorMessage = messageKey ? t(messageKey, { title: foundTitle }) : undefined;

  const files: DropZoneFile[] = file
    ? [
        {
          name: file.name,
          size: formatFileSize(file.size),
          ...(isUploading
            ? { status: 'uploading' as const, progress: null }
            : errorMessage
              ? { status: 'error' as const }
              : {}),
        },
      ]
    : [];

  return (
    <Dialog
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen && !isUploading) close();
      }}
      closeable={!isUploading}
      closeOnEscape={!isUploading}
      closeOnInteractOutside={false}
      footerType="none"
      // The design system's dialog has a fixed height and clips its body. This one is as tall as
      // its legal text and drop zone, and scrolls only when the window is shorter than that.
      type="desktop-scrollable"
      className="h-auto"
      title={t(`${T_PREFIX}transcript-upload-dialog-title`)}
      description={t(`${T_PREFIX}transcript-upload-dialog-description`)}
    >
      <div className="flex flex-col gap-4">
        <Callout variant="info" icon={Info}>
          <LegalWarning />
        </Callout>
        <DropZone
          accept={TRANSCRIPT_UPLOAD_ACCEPT}
          actionLabel=""
          maxFiles={1}
          disabled={isUploading}
          invalid={Boolean(errorMessage)}
          title={t(`${T_PREFIX}transcript-upload-dropzone-title`)}
          description={t(`${T_PREFIX}transcript-upload-hint`)}
          files={files}
          onFileAccept={({ files: accepted }) => {
            if (accepted[0]) void submit(accepted[0]);
          }}
          // A wrong type is refused by the zone itself; the same check as the upload words it.
          onFileReject={({ files: rejected }) => {
            if (rejected[0]) void submit(rejected[0].file);
          }}
          onFileRemove={() => {
            setFile(null);
            onClearError();
          }}
        />
        {errorMessage && (
          <Callout variant="negative" role="alert">
            {errorMessage}
          </Callout>
        )}
      </div>
    </Dialog>
  );
};
